/**
 * Audit Context Form Component
 * Always-visible form for providing context metadata (medium type, audience, etc.)
 * Used in: Upload page
 */

import {
  Briefcase,
  Building2,
  GraduationCap,
  Handshake,
  UserCheck,
  Users,
} from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CardRadio } from "@/features/upload/components/CardRadio";
import { useAuditContextOptions } from "@/hooks/useAuditContextOptions";
import { MARKET_OPTIONS } from "@vardic/shared/types.ts";
import { INDUSTRY_SECTOR_OPTIONS } from "@/constants/company-form-options";
import type { CommunicationPurpose } from "@/types";
import type { AuditContextFormValues } from "../audit-context-form";

interface AuditContextFormProps {
  values: AuditContextFormValues;
  onChange: (values: AuditContextFormValues) => void;
  disabled?: boolean;
  /**
   * Fired whenever the Q1-answered signal flips. True iff `communicationPurpose`
   * is set (one of the two valid values). The parent page uses this to gate the
   * submit button at the page level — Q1 is the mandatory first question in
   * csr-context-declaration-gate spec, and a submission that bypasses it must
   * not enter the pipeline (pirx-c4dlb).
   */
  onQ1AnsweredChange?: (answered: boolean) => void;
  /**
   * The user's per-user default target market. `geographicScope` inherits it
   * silently; when the per-run value differs the form shows an "overridden"
   * badge and a reset affordance. Defaults to Polska for anonymous / first load.
   */
  defaultMarket?: string;
}
// Decorative only — the card's visible label carries the meaning (F11).
const TARGET_AUDIENCE_ICONS: Record<string, typeof Users> = {
  general_consumers: Users,
  existing_customers: UserCheck,
  business_customers: Building2,
  internal_employees: Briefcase,
};
export function AuditContextForm({
  values,
  onChange,
  disabled = false,
  onQ1AnsweredChange,
  defaultMarket = "Polska",
}: AuditContextFormProps) {
  const { t } = useTranslation(["upload"]);
  const { mediumTypeOptions, targetAudienceOptions } =
    useAuditContextOptions();
  const marketOverridden = values.geographicScope !== defaultMarket;
  const handleChange = (
    field: keyof AuditContextFormValues,
    value: string,
  ) => {
    onChange({ ...values, [field]: value });
  };

  // Q2 (targetAudience) only renders when Q1 is "commercial" (see below); once
  // Q1 switches away from "commercial" the picker disappears and the user has
  // no way to see or change a previously-selected audience. Left uncleared,
  // that stale value would still reach the backend's binding B2B-exemption
  // gate (pre-screening.ts Gate 1), which runs unconditionally on
  // `targetAudience` regardless of `communicationPurpose` — silently
  // short-circuiting a "csr" audit that briefly touched a B2B audience.
  const handleCommunicationPurposeChange = (value: string) => {
    onChange({
      ...values,
      communicationPurpose: value as CommunicationPurpose,
      targetAudience: value === "commercial" ? values.targetAudience : "",
    });
  };

  // Form-locking (D0b, AC #3): downstream fields stay disabled until Q1 is
  // answered, and — when Q1 is "commercial" — until Q2 is answered too.
  // csr-mode (educational_csr) doesn't need an audience, so Q2 is skipped.
  const q1Answered = values.communicationPurpose !== "";
  const isCommercial = values.communicationPurpose === "commercial";
  const q2Answered = !isCommercial || values.targetAudience !== "";
  const downstreamDisabled = disabled || !q1Answered || !q2Answered;

  // pirx-c4dlb: surface the Q1-answered signal to the parent page so it can
  // gate the submit button. Reuse the same `q1Answered` boolean computed
  // above (NOT a parallel check) — the page-level disable, the form's
  // downstreamDisabled lock, and the eventual server-side 422 all key off
  // the same single source of truth.
  useEffect(() => {
    onQ1AnsweredChange?.(q1Answered);
  }, [q1Answered, onQ1AnsweredChange]);

  return (
    <div data-test="audit-context-form">
      <div
        className="space-y-5 rounded-[var(--r-lg)] border border-[var(--border)] bg-[color:color-mix(in_srgb,var(--card)_92%,transparent)] p-5"
        data-test="context-form-content"
      >
        <div>
          <h2 className="font-semibold text-[var(--foreground)]">
            {t("upload:context.sectionTitle")}
          </h2>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            {t("upload:context.sectionDescription")}
          </p>
        </div>

        {/* Q1: communicationPurpose — two-card picker (AC #2) */}
        <div role="radiogroup" aria-labelledby="context-purpose-label">
          <h3
            id="context-purpose-label"
            className="mb-2 block text-sm font-medium text-[var(--foreground)]"
          >
            {t("upload:context.communicationPurpose.label")}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <CardRadio
              data-test="context-purpose-commercial"
              name="communicationPurpose"
              value="commercial"
              selected={values.communicationPurpose === "commercial"}
              title={t("upload:context.communicationPurpose.commercial.title")}
              description={t(
                "upload:context.communicationPurpose.commercial.description",
              )}
              icon={<Handshake size={18} />}
              onSelect={handleCommunicationPurposeChange}
              disabled={disabled}
              tabIndex={
                values.communicationPurpose === "commercial" ||
                  values.communicationPurpose === ""
                  ? 0
                  : -1
              }
            />
            <CardRadio
              data-test="context-purpose-csr"
              name="communicationPurpose"
              value="educational_csr"
              selected={values.communicationPurpose === "educational_csr"}
              title={t(
                "upload:context.communicationPurpose.educational_csr.title",
              )}
              description={t(
                "upload:context.communicationPurpose.educational_csr.description",
              )}
              icon={<GraduationCap size={18} />}
              onSelect={handleCommunicationPurposeChange}
              disabled={disabled}
              tabIndex={
                values.communicationPurpose === "educational_csr" ? 0 : -1
              }
            />
          </div>
        </div>

        {/* Q2: targetAudience — conditional card picker, only when Q1 = commercial */}
        {isCommercial && (
          <div
            role="radiogroup"
            aria-labelledby="context-target-audience-label"
          >
            <h3
              id="context-target-audience-label"
              className="mb-2 block text-sm font-medium text-[var(--foreground)]"
            >
              {t("upload:context.targetAudience.label")}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {targetAudienceOptions
                .filter((option) => option.value !== "")
                .map((option, index) => {
                  const Icon = TARGET_AUDIENCE_ICONS[option.value] ?? Users;
                  return (
                    <CardRadio
                      key={option.value}
                      data-test={`context-target-audience-${option.value}`}
                      name="targetAudience"
                      value={option.value}
                      selected={values.targetAudience === option.value}
                      title={option.label}
                      description={t(
                        `upload:context.targetAudience.cards.${option.value}.description`,
                      )}
                      icon={<Icon size={18} />}
                      onSelect={(value) =>
                        handleChange("targetAudience", value)}
                      disabled={disabled}
                      tabIndex={
                        values.targetAudience === option.value ||
                          (index === 0 && values.targetAudience === "")
                          ? 0
                          : -1
                      }
                    />
                  );
                })}
            </div>
          </div>
        )}

        {/* Medium Type */}
        <div>
          <label
            htmlFor="mediumType"
            className="mb-1 block text-sm font-medium text-[var(--foreground)]"
          >
            {t("upload:context.mediumType.label")}
          </label>
          <select
            id="mediumType"
            value={values.mediumType}
            onChange={(e) => handleChange("mediumType", e.target.value)}
            disabled={downstreamDisabled}
            className="w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-[var(--foreground)] focus:border-[var(--ring)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)] disabled:cursor-not-allowed disabled:opacity-50"
            data-test="context-medium-type"
          >
            {mediumTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {/* Audited product / service domain (optional) — per-audit explicit declaration (NACE-section select) */}
        <div>
          <label
            htmlFor="industrySector"
            className="mb-1 block text-sm font-medium text-[var(--foreground)]"
          >
            {t("upload:context.industrySector.label")}
          </label>
          <p
            id="context-industry-sector-hint"
            className="mb-2 text-xs text-[var(--muted-foreground)]"
          >
            {t("upload:context.industrySector.hint")}
          </p>
          <select
            id="industrySector"
            aria-describedby="context-industry-sector-hint"
            value={values.industrySector}
            onChange={(e) => handleChange("industrySector", e.target.value)}
            disabled={downstreamDisabled}
            className="w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-[var(--foreground)] focus:border-[var(--ring)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)] disabled:cursor-not-allowed disabled:opacity-50"
            data-test="context-industry-sector"
          >
            <option value="">
              {t("upload:context.industrySector.placeholder")}
            </option>
            {INDUSTRY_SECTOR_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
                data-test={`context-industry-sector-option-${option.value.toLowerCase()}`}
              >
                {t(option.labelKey)}
              </option>
            ))}
          </select>
        </div>

        {/* Target market — inherits the per-user default; overridable per run */}
        <div>
          <div className="mb-1 flex items-center justify-between gap-2">
            <label
              htmlFor="geographicScope"
              className="block text-sm font-medium text-[var(--foreground)]"
            >
              {t("upload:context.geographicScope.label")}
            </label>
            {marketOverridden && (
              <span className="flex items-center gap-2">
                <span
                  className="rounded-full bg-[color:color-mix(in_srgb,var(--info)_16%,transparent)] px-2 py-0.5 text-xs font-medium text-[var(--info)]"
                  data-test="context-market-overridden"
                >
                  {t("upload:context.geographicScope.overridden")}
                </span>
                <button
                  type="button"
                  onClick={() => handleChange("geographicScope", defaultMarket)}
                  disabled={downstreamDisabled}
                  className="text-xs font-medium text-[var(--primary)] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                  data-test="context-market-reset"
                >
                  {t("upload:context.geographicScope.reset")}
                </button>
              </span>
            )}
          </div>
          <select
            id="geographicScope"
            value={values.geographicScope}
            onChange={(e) => handleChange("geographicScope", e.target.value)}
            disabled={downstreamDisabled}
            className="w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-[var(--foreground)] focus:border-[var(--ring)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)] disabled:cursor-not-allowed disabled:opacity-50"
            data-test="context-geographic-scope"
          >
            {/* Keep any legacy free-text value selectable so it is not lost */}
            {!MARKET_OPTIONS.includes(
                  values.geographicScope as (typeof MARKET_OPTIONS)[number],
                ) && values.geographicScope && (
              <option value={values.geographicScope}>
                {values.geographicScope}
              </option>
            )}
            {MARKET_OPTIONS.map((market) => (
              <option key={market} value={market}>
                {market}
              </option>
            ))}
          </select>
        </div>

        {/* Publication Date (optional) */}
        <div>
          <label
            htmlFor="publicationDate"
            className="mb-1 block text-sm font-medium text-[var(--foreground)]"
          >
            {t("upload:context.publicationDate.label")}
          </label>
          <input
            type="date"
            id="publicationDate"
            value={values.publicationDate}
            onChange={(e) => handleChange("publicationDate", e.target.value)}
            disabled={downstreamDisabled}
            className="w-full rounded-[var(--r-sm)] border border-[var(--input)] bg-[var(--background)] px-3 py-2 text-[var(--foreground)] focus:border-[var(--ring)] focus:outline-none focus:ring-2 focus:ring-[color:color-mix(in_srgb,var(--ring)_30%,transparent)] disabled:cursor-not-allowed disabled:opacity-50"
            data-test="context-publication-date"
          />
        </div>
      </div>
    </div>
  );
}
